import { RouteLocationNormalized, Router, START_LOCATION } from 'vue-router'
import { RecordViewBody } from '@/interfaces/user-activity/UserActivity.dao'
import AuthService from '@/plugins/api-plugin/services/auth.service'
import UserActivityService from '@/plugins/api-plugin/services/user-activity.service'

/** A batch is sent at this many views, the server's limit per request. */
export const FLUSH_SIZE = 20

/** A started batch is sent at the latest after this delay. */
export const FLUSH_MS = 15_000

/** The routes that are not pages a person works on: the tracker records no view of them. */
export const UNTRACKED_ROUTES = ['page-not-found', 'redirecting', 'sign.in']

const LIMITS = { name: 100, id: 64, path: 500 }

const text = (value: unknown, max: number) => (typeof value === 'string' && value ? value.slice(0, max) : undefined)

/**
 * The view one navigation records, or `null` when it records none: an unnamed or skipped route, or
 * a change of the query string only. Values are cut to the server's limits, which reject the batch.
 */
export const viewOf = (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    at = new Date(),
): RecordViewBody | null => {
    const route = text(to.name, LIMITS.name)
    if (!route || UNTRACKED_ROUTES.includes(route)) return null
    if (to.name === from.name && to.path === from.path) return null
    return {
        route,
        entityId: text(to.params.id ?? to.params.psoqNumber, LIMITS.id),
        from: text(from.name, LIMITS.name),
        path: to.path.slice(0, LIMITS.path),
        at: at.toISOString(),
    }
}

type ViewSender = Pick<UserActivityService, 'recordViews' | 'sendViewsOnLeave'>

/**
 * Records the Portal page opens for the activity log: the current page and each later navigation
 * are buffered and sent in batches of 20 or after 15 s, and the rest goes out with a keepalive
 * request when the page is left. Nothing is sent without a sign-in token.
 */
export default function useActivityTracker(router: Router, service: ViewSender) {
    let buffer: RecordViewBody[] = []
    let timer: ReturnType<typeof setTimeout> | null = null

    const take = () => {
        if (timer) clearTimeout(timer)
        timer = null
        const views = buffer
        buffer = []
        return AuthService.getAuthToken() ? views : []
    }

    const flush = () => {
        const views = take()
        if (views.length) void service.recordViews(views)
    }

    const leave = () => {
        const views = take()
        if (views.length) service.sendViewsOnLeave(views)
    }

    const record = (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
        const view = viewOf(to, from)
        if (!view) return
        buffer.push(view)
        if (buffer.length >= FLUSH_SIZE) flush()
        else timer ??= setTimeout(flush, FLUSH_MS)
    }

    record(router.currentRoute.value, START_LOCATION)
    const removeHook = router.afterEach((to, from, failure) => {
        if (!failure) record(to, from)
    })
    window.addEventListener('pagehide', leave)

    const stop = () => {
        removeHook()
        window.removeEventListener('pagehide', leave)
        if (timer) clearTimeout(timer)
        timer = null
    }

    return { flush, stop }
}
